import { useState } from "react";
import { updateDriverProfile } from "../../lib/driverApi";
import type { DriverProfile, DriverProfileUpdate } from "../../lib/driverApi";
import { useDriver } from "../../lib/driverContext";
import { errorText } from "../../lib/api";
import { toast } from "../../lib/toast";
import { Card, CardTitle, PageTitle } from "../../components/company/ui";
import ChangeEmail from "../../components/ui/ChangeEmail";
import { longDate } from "../../lib/format";
import { useT } from "../../i18n";

const input = "h-10 w-full rounded border border-transparent bg-surface px-3 text-sm outline-none focus:border-brand disabled:text-muted";
const day = (v?: string) => (v ? v.slice(0, 10) : "");

/** Driver profile: personal details (editable) and professional details. */
export default function DriverProfilePage() {
  const t = useT();
  const { driver } = useDriver();
  if (!driver) return <p className="text-sm text-muted">{t.common.loading}</p>;
  return <ProfileForm key={driver.email} driver={driver} />;
}

function ProfileForm({ driver }: { driver: DriverProfile }) {
  const t = useT();
  const p = t.driverPages.profile;
  const { setDriver } = useDriver();
  const initial: Required<DriverProfileUpdate> = {
    firstName: driver.firstName,
    lastName: driver.lastName,
    phone: driver.phone ?? "",
    address: driver.address ?? "",
    city: driver.city ?? "",
    postalCode: driver.postalCode ?? "",
    language: driver.language ?? "fr",
    vtcCardExpiry: day(driver.vtcCardExpiry),
  };
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const dirty = JSON.stringify(form) !== JSON.stringify(initial);

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { profile, message } = await updateDriverProfile(form);
      setDriver({ ...driver, ...profile });
      toast.success(message);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  const field = (label: string, k: keyof typeof form, type = "text", placeholder?: string) => (
    <label className="grid gap-1 text-[13px] text-muted">
      {label}
      <input type={type} value={form[k]} onChange={set(k)} disabled={busy} placeholder={placeholder} className={input} />
    </label>
  );

  return (
    <form onSubmit={save} className="grid max-w-200 gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <PageTitle className="mb-0">{p.title}</PageTitle>
        <span className="rounded bg-white px-2.5 py-1 text-xs font-medium text-ink-soft">{p.status[driver.status]}</span>
      </div>

      <Card className="grid gap-3.5">
        <CardTitle>{p.personal}</CardTitle>
        <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2">
          {field(p.firstName, "firstName", "text", t.common.ph.firstName)}
          {field(p.lastName, "lastName", "text", t.common.ph.lastName)}
          <ChangeEmail label={p.email} email={driver.email} hint={p.emailHint} onChanged={(email) => setDriver({ ...driver, email })} />
          {field(p.phone, "phone", "tel", t.common.ph.phone)}
          {field(p.address, "address", "text", t.common.ph.street)}
          {field(p.city, "city", "text", t.common.ph.city)}
          {field(p.postalCode, "postalCode", "text", t.common.ph.postalCode)}
          <label className="grid gap-1 text-[13px] text-muted">
            {p.language}
            <select value={form.language} onChange={set("language")} disabled={busy} className={input}>
              <option value="fr">Français</option>
              <option value="en">English</option>
            </select>
          </label>
        </div>
      </Card>

      <Card className="grid gap-3.5">
        <CardTitle>{p.professional}</CardTitle>
        <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2">
          <label className="grid gap-1 text-[13px] text-muted">
            {p.licenceNumber}
            <input value={driver.licenceNumber ?? ""} disabled className={input} />
          </label>
          <label className="grid gap-1 text-[13px] text-muted">
            {p.licenceDate}
            <input value={driver.licenceDate ? longDate(day(driver.licenceDate)) : ""} disabled className={input} />
          </label>
          <label className="grid gap-1 text-[13px] text-muted">
            {p.vtcCardNumber}
            <input value={driver.vtcCardNumber ?? ""} disabled className={input} />
          </label>
          {field(p.vtcCardExpiry, "vtcCardExpiry", "date")}
        </div>
      </Card>

      {error && <p className="m-0 text-sm text-tone-red-fg">{error}</p>}
      <div>
        <button
          type="submit"
          disabled={busy || !dirty}
          className="h-10 cursor-pointer rounded border-0 bg-brand px-5 text-sm font-medium text-white hover:bg-brand-dark disabled:cursor-not-allowed disabled:opacity-50"
        >
          {busy ? p.saving : p.save}
        </button>
      </div>
    </form>
  );
}
